
import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useClose } from "@/contexts/CloseContext";
import { CloseApiService } from "@/services/closeApi/closeApiService";
import { Loader2, RefreshCw, AlertCircle, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

export const CloseOpportunities = () => {
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isConnected, apiKey } = useClose();

  const fetchOpportunities = async () => {
    if (!isConnected || !apiKey) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await CloseApiService.getOpportunities(apiKey);
      setOpportunities(data);
    } catch (err: any) {
      console.error("Error fetching Close opportunities:", err);
      setError(err.message || "Fehler beim Laden der Verkaufschancen");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected && apiKey) {
      fetchOpportunities();
    }
  }, [isConnected, apiKey]);

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('won')) return 'bg-green-500/20 text-green-500';
    if (statusLower.includes('qualified')) return 'bg-blue-500/20 text-blue-500';
    if (statusLower.includes('progress') || statusLower.includes('negotiation')) return 'bg-amber-500/20 text-amber-500';
    if (statusLower.includes('lost')) return 'bg-red-500/20 text-red-500';
    return 'bg-gray-500/20 text-gray-500';
  };

  if (!isConnected) {
    return (
      <Card className="bg-gray-800/20 border-gray-700">
        <CardContent className="p-6 text-center">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Nicht verbunden</h3>
          <p className="text-gray-400 mb-4">
            Verbinden Sie Ihr Close CRM-Konto, um Ihre Verkaufschancen zu sehen.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Verkaufschancen</h3>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchOpportunities}
          disabled={isLoading}
          className="border-gray-700"
        >
          {isLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <RefreshCw size={16} />
          )}
        </Button>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-900 text-red-400 px-4 py-3 rounded flex items-center gap-2">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : opportunities.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {opportunities.map((opportunity) => (
            <Card key={opportunity.id} className="bg-gray-800/20 border-gray-700">
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-medium truncate flex-1">{opportunity.lead_name || 'Verkaufschance'}</h4>
                  <Badge className={`ml-2 ${getStatusColor(opportunity.status_label)}`}>
                    {opportunity.status_label}
                  </Badge>
                </div>
                <div className="flex justify-between items-center text-sm mb-2">
                  <span className="text-gray-400">Wert:</span>
                  <span className="font-medium text-green-500">{opportunity.value_formatted}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Wahrscheinlichkeit</span>
                    <span>{opportunity.confidence}%</span>
                  </div>
                  <Progress value={opportunity.confidence} className="h-1.5" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-10 text-gray-500">
          <DollarSign className="w-10 h-10 mx-auto mb-4 opacity-50" />
          <p>Keine Verkaufschancen gefunden</p>
        </div>
      )}
    </div>
  );
};
